import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Mail, Phone, Clock, Send, Loader2, TreePine, Award, Users, Heart } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { REGISTERED_CHARITY_LABEL } from '../constants/charity';
import { PHONE_NUMBER, PHONE_TEL_HREF } from '../constants/contact';
import { scheduleScrollToElementById } from '../constants/scrollNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ABOUT_HASH_SCROLL_IDS = ['story', 'team', 'press', 'contact'];

const team = [
  { name: 'Ron Simpson BEM', role: 'Chair', image: '/images/governance/img2.png?v=3' },
  { name: 'Rachel Burkitt', role: 'Vice Chair', image: '/images/governance/img3.png?v=3' },
  { name: 'Carolyn Cartwright & Cliff Bacon', role: 'Planning Team', image: '/images/governance/img4.png?v=3' },
  { name: 'Geoff Clyde', role: 'Administrator', image: '/images/governance/img8.png?v=3' },
];

const AboutPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const scrollId = location.hash.slice(1);
    if (!ABOUT_HASH_SCROLL_IDS.includes(scrollId)) return;
    return scheduleScrollToElementById(scrollId, { delayMs: 50 });
  }, [location.pathname, location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully!', {
        description: 'We\'ll get back to you soon.',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="about-page">
        {/* Hero */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/cpre images for website/22.png"
              alt="Countryside"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-countryside-green/80" />
          </div>
          <div className="relative container-custom text-center text-white">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Badge className="bg-cpre-harvest-gold text-cpre-new-yew hover:bg-cpre-autumn-leaf hover:text-white w-14 h-14 rounded-2xl inline-flex items-center justify-center p-0">
                <TreePine className="w-8 h-8" />
              </Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-6 text-white">About CPRE</h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Rutland & Lincolnshire — Protecting the countryside for 100 years
            </p>
            <p className="text-white/75 text-sm mt-4">{REGISTERED_CHARITY_LABEL}</p>
          </div>
        </section>

        {/* Our Story */}
        <section id="story" className="section-padding">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-countryside-terracotta font-medium text-sm uppercase tracking-wider">Our Story</span>
                <h2 className="mt-2 mb-6">A Century of Conservation</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    The Campaign to Protect Rural England (CPRE) has been at the forefront of countryside conservation since 1926. Our Rutland & Lincolnshire branch has been an active part of this movement, working tirelessly to protect the unique landscapes and communities of our region.
                  </p>
                  <p>
                    From the rolling hills of the Lincolnshire Wolds to the tranquil waters of Rutland Water, we've campaigned against inappropriate development, preserved green spaces, and advocated for sustainable rural communities.
                  </p>
                  <p>
                    As we celebrate our centenary, we remain committed to our founding mission while adapting to new challenges such as climate change, biodiversity loss, and the pressures of modern development.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="/images/cpre images for website/13.jpg"
                  alt="Lincolnshire countryside"
                  className="rounded-2xl shadow-lg"
                />
                <div className="absolute -bottom-6 -left-6 bg-countryside-green text-white p-6 rounded-2xl shadow-lg">
                  <span className="text-4xl font-heading font-bold">100</span>
                  <span className="block text-sm text-white/80">Years of Protection</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do */}
        <section className="section-padding bg-countryside-bone/30">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-countryside-terracotta font-medium text-sm uppercase tracking-wider">What We Do</span>
              <h2 className="mt-2">Our Work</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="bg-white border-stone-200">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-countryside-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-countryside-green" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-3">Planning Advocacy</h3>
                  <p className="text-muted-foreground">
                    We scrutinize planning applications and campaign against developments that threaten our countryside's character.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white border-stone-200">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-countryside-terracotta/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <TreePine className="w-8 h-8 text-countryside-terracotta" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-3">Conservation Projects</h3>
                  <p className="text-muted-foreground">
                    From habitat restoration to trail maintenance, we actively work to improve and protect our local environment.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white border-stone-200">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Users className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl mb-3">Community Engagement</h3>
                  <p className="text-muted-foreground">
                    We organize events, walks, and educational programs to connect people with their local countryside.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Our Team */}
        <section id="team" className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="text-countryside-terracotta font-medium text-sm uppercase tracking-wider">Our People</span>
              <h2 className="mt-2">Meet the Team</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {team.map((member, index) => (
                <div key={index} className="text-center" data-testid={`team-member-${index}`}>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h4 className="font-heading font-semibold">{member.name}</h4>
                  <p className="text-sm text-countryside-green">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="section-padding bg-countryside-bone/30" data-testid="contact-section">
          <span id="press" className="block h-0 overflow-hidden" aria-hidden="true" />
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <span className="text-countryside-terracotta font-medium text-sm uppercase tracking-wider">Get in Touch</span>
                <h2 className="mt-2 mb-6">Contact Us</h2>
                <p className="text-muted-foreground mb-8">
                  Have a question or want to get involved? We'd love to hear from you.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-countryside-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-countryside-green" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Email</h4>
                      <p className="text-muted-foreground">chair@cprerutlandandlincs.uk</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-countryside-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-countryside-green" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Phone</h4>
                      <a href={PHONE_TEL_HREF} className="text-muted-foreground hover:text-countryside-green hover:underline">
                        {PHONE_NUMBER}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-countryside-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="w-5 h-5 text-countryside-green" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Charity registration</h4>
                      <p className="text-muted-foreground">{REGISTERED_CHARITY_LABEL}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-countryside-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-countryside-green" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Address</h4>
                      <p className="text-muted-foreground">
                        CPRE Rutland & Lincolnshire<br />
                        The Old School House<br />
                        Main Street, Oakham<br />
                        Rutland, LE15 6AA
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <Card className="bg-white border-stone-200">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact-name">Name *</Label>
                        <Input
                          id="contact-name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-2 rounded-lg"
                          data-testid="contact-name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-email">Email *</Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-2 rounded-lg"
                          data-testid="contact-email"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="contact-subject">Subject *</Label>
                      <Input
                        id="contact-subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="mt-2 rounded-lg"
                        data-testid="contact-subject"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="contact-message">Message *</Label>
                      <Textarea
                        id="contact-message"
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="mt-2 rounded-lg"
                        data-testid="contact-message"
                        required
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="w-full bg-countryside-green hover:bg-countryside-green-dark text-white rounded-lg h-12"
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AboutPage;
